import IconButton from '@/components/ui/custom/buttons/IconButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import useToggle from '@/data/hook/useToggle';
import CoursePath from '@/models/CoursePath';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { motion } from 'motion/react';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import CourseBoxHeaderCollapsed from './CourseBoxHeaderCollapsed';
import CourseBoxHeaderExpanded from './CourseBoxHeaderExpanded';
import { CourseBoxHeaderProps } from './CourseBoxHeader';

export type CourseHeaderFields = Pick<CoursePath, 'course' | 'lessonsQuantity'>;

const ChevronIconMotion = motion.create(ChevronRightIcon);

interface CourseBoxHeaderFormProps extends CourseBoxHeaderProps {
  onSubmit: (params: { coursePath: CoursePath; validated: boolean }) => void;
}

export default function CourseBoxHeaderForm({
  coursePath,
  isLoading: loading,
  onSubmit,
}: CourseBoxHeaderFormProps) {
  const { isOpen, toggle } = useToggle();
  const methods = useForm<CourseHeaderFields>({
    defaultValues: coursePath,
  });

  useEffect(() => {
    const subscription = methods.watch(coursePath =>
      onSubmit({ coursePath: coursePath as CoursePath, validated: true }),
    );
    return () => {
      subscription.unsubscribe();
    };
  }, [methods, onSubmit]);

  return (
    <FormProvider {...methods}>
      <header className="relative flex w-full max-w-full shadow-default rounded-xl border-neutral-content border px-1.5 py-2 overflow-x-hidden">
        <IconButton
          icon={
            <ChevronIconMotion
              className="w-10 h-10 mt-1 text-primary"
              animate={{ rotate: isOpen ? 90 : 0 }}
              transition={{ duration: 0.1, ease: 'easeInOut' }}
            />
          }
          onClick={toggle}
        />
        <ConditionalRenderer condition={!isOpen}>
          <CourseBoxHeaderCollapsed
            coursePath={coursePath}
            isLoading={loading}
          />
        </ConditionalRenderer>
        <ConditionalRenderer condition={isOpen}>
          <CourseBoxHeaderExpanded
            coursePath={coursePath}
            isLoading={loading}
          />
        </ConditionalRenderer>
      </header>
    </FormProvider>
  );
}
